import { useTranslation } from 'react-i18next';
import { categoryOptionLabel, categorySlug } from '@/lib/category';
import type { CategoryOption } from '@/types';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AdminApiError } from '@/services/adminApi';
import type { EventPayload, EventType } from '@/types/admin';

const TEXTAREA_CLASS = 'field-control min-h-[90px]';
const SELECT_CLASS = 'field-control h-10';

function numberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    return null;
  }
  return parsed;
}

/* Form field: small-caps label above the control, hint or error below it. */
function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="kicker block">
        {label}
      </label>
      {children}
      {hint && !error && <p className="m-0 text-xs text-ink-2">{hint}</p>}
      {error && (
        <p className="m-0 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function EventForm({
  initial,
  typeEditable,
  categories,
  categoriesLoading = false,
  categoriesLoadFailed = false,
  submitLabel,
  onSubmit,
}: {
  initial: EventPayload;
  typeEditable: boolean;
  categories: CategoryOption[];
  categoriesLoading?: boolean;
  categoriesLoadFailed?: boolean;
  submitLabel: string;
  onSubmit: (payload: EventPayload) => Promise<void>;
}) {
  const { t } = useTranslation('admin');
  const [values, setValues] = useState<EventPayload>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState('');

  const set = <K extends keyof EventPayload>(key: K, value: EventPayload[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    if (creatingCategory && !categoryDraft.trim()) {
      setFieldErrors({ category: 'common:validation.required' });
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const payload = {
        ...values,
        category: categorySlug(creatingCategory ? categoryDraft.trim() : values.category, categories),
      };
      await onSubmit(payload);
      setValues(payload);
      setCreatingCategory(false);
      setCategoryDraft('');
    } catch (reason) {
      if (reason instanceof AdminApiError) {
        setFieldErrors(reason.fields);
        setFormError(reason.message);
      } else {
        setFormError('form.saveFailed');
      }
    } finally {
      setSaving(false);
    }
  };

  const isAnnual = values.event_type === 'annual';
  const selectedCategory = categorySlug(values.category, categories);

  let categoryError: string | undefined;
  if (fieldErrors.category) {
    categoryError = t(fieldErrors.category, { defaultValue: fieldErrors.category });
  } else if (categoriesLoadFailed) {
    categoryError = t('form.categoriesLoadFailed');
  }

  let categoryToggleLabel = t('form.newCategory');
  if (creatingCategory) {
    categoryToggleLabel = t('form.chooseCategory');
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {typeEditable && (
        <fieldset className="space-y-1.5">
          <legend className="kicker">{t('fields.event_type')}</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1 text-sm">
            {(['annual', 'specific'] as EventType[]).map((type) => (
              <label key={type} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="event_type"
                  value={type}
                  checked={values.event_type === type}
                  onChange={() => set('event_type', type)}
                  className="accent-ink"
                />
                {t(`form.${type}`)}
              </label>
            ))}
          </div>
          <p className="m-0 text-xs text-ink-2">{t('form.typeHint')}</p>
        </fieldset>
      )}

      <Field label={t('fields.title')} htmlFor="title" error={fieldErrors.title ? t(fieldErrors.title, { defaultValue: fieldErrors.title }) : undefined}>
        <Input
          id="title"
          value={values.title}
          onChange={(event) => set('title', event.target.value)}
          maxLength={200}
          required
        />
      </Field>

      {isAnnual && (
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('fields.month')} htmlFor="month" error={fieldErrors.month ? t(fieldErrors.month, { defaultValue: fieldErrors.month }) : undefined}>
            <Input
              id="month"
              type="number"
              min={1}
              max={12}
              value={values.month ?? ''}
              onChange={(event) => set('month', numberOrNull(event.target.value))}
              className="font-mono"
            />
          </Field>
          <Field label={t('fields.day')} htmlFor="day" error={fieldErrors.day ? t(fieldErrors.day, { defaultValue: fieldErrors.day }) : undefined}>
            <Input
              id="day"
              type="number"
              min={1}
              max={31}
              value={values.day ?? ''}
              onChange={(event) => set('day', numberOrNull(event.target.value))}
              className="font-mono"
            />
          </Field>
          <Field label={t('fields.year')} htmlFor="year" error={fieldErrors.year ? t(fieldErrors.year, { defaultValue: fieldErrors.year }) : undefined} hint={t('form.yearHint')}>
            <Input
              id="year"
              type="number"
              min={1}
              max={9999}
              value={values.year ?? ''}
              onChange={(event) => set('year', numberOrNull(event.target.value))}
              className="font-mono"
            />
          </Field>
        </div>
      )}
      {!isAnnual && (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('fields.start_date')} htmlFor="start_date" error={fieldErrors.start_date ? t(fieldErrors.start_date, { defaultValue: fieldErrors.start_date }) : undefined}>
            <Input
              id="start_date"
              type="date"
              value={values.start_date}
              onChange={(event) => set('start_date', event.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label={t('fields.end_date')} htmlFor="end_date" error={fieldErrors.end_date ? t(fieldErrors.end_date, { defaultValue: fieldErrors.end_date }) : undefined} hint={t('form.endDateHint')}>
            <Input
              id="end_date"
              type="date"
              value={values.end_date}
              onChange={(event) => set('end_date', event.target.value)}
              className="font-mono"
            />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('fields.category')} htmlFor="category" error={categoryError}>
          {creatingCategory ? (
            <Input
              id="category"
              value={categoryDraft}
              onChange={(event) => setCategoryDraft(event.target.value)}
              maxLength={50}
              placeholder={t('form.categoryName')}
              autoFocus
            />
          ) : (
            <select
              id="category"
              className={SELECT_CLASS}
              value={selectedCategory}
              onChange={(event) => set('category', event.target.value)}
            >
              <option value="">{t('form.noCategory')}</option>
              {selectedCategory && !categories.some((category) => category.slug === selectedCategory) && (
                <option value={selectedCategory}>{values.category}</option>
              )}
              {categories.map((category) => (
                <option key={category.slug} value={category.slug}>{categoryOptionLabel(category)}</option>
              ))}
            </select>
          )}
          <Button
            type="button"
            variant="link"
            className="text-xs"
            disabled={!creatingCategory && (categoriesLoading || categoriesLoadFailed)}
            onClick={() => {
              setCreatingCategory((current) => !current);
              setFieldErrors((current) => {
                const next = { ...current };
                delete next.category;
                return next;
              });
            }}
          >
            {categoryToggleLabel}
          </Button>
        </Field>
        <Field label={t('fields.tags')} htmlFor="tags" error={fieldErrors.tags ? t(fieldErrors.tags, { defaultValue: fieldErrors.tags }) : undefined} hint={t('form.tagsHint')}>
          <Input
            id="tags"
            value={values.tags}
            onChange={(event) => set('tags', event.target.value)}
            maxLength={300}
          />
        </Field>
      </div>

      <Field label={t('fields.location')} htmlFor="location" error={fieldErrors.location ? t(fieldErrors.location, { defaultValue: fieldErrors.location }) : undefined}>
        <Input
          id="location"
          value={values.location}
          onChange={(event) => set('location', event.target.value)}
          maxLength={200}
        />
      </Field>

      <Field label={t('fields.url')} htmlFor="url" error={fieldErrors.url ? t(fieldErrors.url, { defaultValue: fieldErrors.url }) : undefined}>
        <Input
          id="url"
          type="url"
          placeholder="https://"
          value={values.url}
          onChange={(event) => set('url', event.target.value)}
          maxLength={500}
          className="font-mono"
        />
      </Field>

      <Field label={t('fields.description')} htmlFor="description" error={fieldErrors.description ? t(fieldErrors.description, { defaultValue: fieldErrors.description }) : undefined}>
        <textarea
          id="description"
          className={TEXTAREA_CLASS}
          value={values.description}
          onChange={(event) => set('description', event.target.value)}
          maxLength={2000}
        />
      </Field>

      {formError && (
        <p role="alert" className="callout callout-danger m-0">
          {t(formError, { defaultValue: formError })}
        </p>
      )}
      <Button type="submit" disabled={saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitLabel}
      </Button>
    </form>
  );
}
